import { useMemo } from 'react'
import { editionsByDecade, type RawEdition } from './awards'

interface Props {
  editions: RawEdition[]
  selectedId: string | null
  onSelect: (edition: RawEdition) => void
}

/**
 * Desktop parity: a vertical "decade > year" list on large screens and
 * a horizontal chip scroller on small screens. The desktop splits the
 * list into decade headers (e.g. "2020s") and tags each edition with
 * its ordinal (e.g. "98th") when available.
 */
export function CeremonyTimeline({ editions, selectedId, onSelect }: Props) {
  const decades = useMemo(() => editionsByDecade(editions), [editions])

  if (editions.length === 0) {
    return (
      <div className="px-3 py-4 text-xs text-text-tertiary">
        No ceremonies available.
      </div>
    )
  }

  return (
    <>
      {/* Desktop sidebar */}
      <nav
        aria-label="Ceremonies"
        className="hidden h-full w-56 shrink-0 overflow-y-auto border-r border-border-primary bg-bg-secondary/40 py-3 lg:block"
      >
        {decades.map(({ decade, editions: eds }) => (
          <div key={decade} className="mb-2">
            <h4 className="px-5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-text-tertiary">
              {decade}
            </h4>
            <ul className="px-2">
              {eds.map((ed) => {
                const active = selectedId === ed.id
                return (
                  <li key={ed.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(ed)}
                      className={`group flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left transition-colors ${
                        active
                          ? 'bg-accent-primary/15 text-accent-primary'
                          : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                      }`}
                    >
                      <span
                        className={`h-4 w-1 rounded-full transition-colors ${
                          active
                            ? 'bg-accent-primary'
                            : 'bg-transparent group-hover:bg-white/20'
                        }`}
                        aria-hidden
                      />
                      <span
                        className={`text-sm tabular-nums ${
                          active ? 'font-bold' : 'font-medium'
                        }`}
                      >
                        {ed.year}
                      </span>
                      {ed.ordinal && (
                        <span
                          className={`ml-auto text-[10px] ${
                            active
                              ? 'text-accent-primary/70'
                              : 'text-text-tertiary opacity-0 transition-opacity group-hover:opacity-60'
                          }`}
                        >
                          {ed.ordinal}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Mobile horizontal picker */}
      <div
        role="tablist"
        aria-label="Ceremonies"
        className="custom-scrollbar flex gap-1.5 overflow-x-auto pb-2 lg:hidden"
      >
        {editions.slice(0, 40).map((ed) => {
          const active = selectedId === ed.id
          return (
            <button
              key={ed.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(ed)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? 'bg-accent-primary text-white'
                  : 'bg-white/5 text-text-secondary hover:bg-white/10 hover:text-text-primary'
              }`}
            >
              {ed.year}
            </button>
          )
        })}
      </div>
    </>
  )
}
